import React from "react"
import {
	Grid,
	Typography,
	Chip,
	useMediaQuery,
	useTheme,
	Tooltip
} from "@material-ui/core"

import {
	Edit as EditIcon,
	Delete as DeleteIcon,
	Block as BlockIcon
} from "@material-ui/icons"

import {
	Divider,
	Portlet
} from "@/components"
import useStyles from "@/pages/Admin/Attendant/components/UserInInstanceCard/styles"
import MoreOptionsMenu from "@/components/MoreOptionsMenu"
import { colors } from "@/styles/colors"
import { UserInInstanceStatus } from "@/protocols/userInInstance"
import { usePermissionStateStore } from "@/store/PermissionState"
import { PERMISSION_DENIED_MESSAGE } from "@/components/PermissionGate/BasePermissionGate"

type TeamDataProps = {
	id: number
	instanceId?: number
	name: string
}

type UserInInstanceCardProps = {
	name?: string
	email?: string,
	status?: UserInInstanceStatus,
	currentRoleName?: string,
	isOwner?: boolean,
	teams?: TeamDataProps[]

	onClickEditButton?: () => void
	onClickDeleteButton?: () => void
	onClickBlockButton?: () => void
}

const UserInInstanceCard: React.FC<UserInInstanceCardProps> = (props) => {
	const { name, email, status, currentRoleName, isOwner, teams, onClickEditButton, onClickDeleteButton, onClickBlockButton } = props

	const MuiTheme = useTheme()
	const classes = useStyles()
	const { checkPermission } = usePermissionStateStore()

	const canDeleteAttendant = checkPermission("attendant", "delete")
	const canBlockAttendant = checkPermission("attendant", "block")
	const canUpdateAttendant = checkPermission("attendant", "update")

	const isSmall = useMediaQuery(MuiTheme.breakpoints.down("xs"))

	return (
		<Portlet
			elevation={1}
		>
			<Grid container justify="space-between" alignItems="center" className={classes.userCard} spacing={6}>
				<Grid
					item xs
					className={classes.textContainer}
				>
					<Typography
						color="textPrimary"
						variant="h2"
						className={classes.nameText}
					>
						{name}
					</Typography>
					<Divider size={1} orientation="horizontal" />
					<Typography
						color="textSecondary"
						variant="body2"
					>
						{email}
					</Typography>
				</Grid>
				<Grid item xs>
					<Grid container justifyContent="flex-start">
						<Grid item>
							<Chip
								className={classes.roleChip}
								label={currentRoleName ?? "Atendente"}
								style={{ background: currentRoleName === "Gestor" ? colors.yellow[100] : colors.purple[100] }}
							/>
						</Grid>
					</Grid>
				</Grid>

				{
					teams?.length ? isSmall && <Divider size={2} orientation="horizontal" /> : <></>
				}

				<Grid
					item xs={5}
					className={classes.textContainer}
				>
					<Grid container spacing={1} justifyContent="flex-end">
						{
							teams?.map((team, index) => (
								<Grid item key={index}>
									<Chip size="medium" label={team?.name} className={classes.teamChip} />
								</Grid>
							))
						}
					</Grid>
				</Grid>

				{ status === "blocked" && (
					<Grid item>
						<Tooltip title={"Bloqueado"} placement="top">
							<BlockIcon style={{ color: colors.red[400] }} />
						</Tooltip>
					</Grid>
				)}
				<Grid item>
					<Grid container justify="flex-end">
						<Grid container className={classes.userCardIconButton} justifyContent="center" alignItems="center">
							<MoreOptionsMenu
								options={[
									{
										title: status === "blocked" ? "Desbloquear" : "Bloquear",
										icon: <BlockIcon />,
										showIcon: true,
										disabled: !canBlockAttendant || isOwner,
										disabledTooltip: isOwner
											? "Não é possível bloquear o dono da conta"
											: PERMISSION_DENIED_MESSAGE,
										onClick: async () => { onClickBlockButton?.() }
									},
									{
										title: "Alterar",
										icon: <EditIcon />,
										showIcon: true,
										disabled: !canUpdateAttendant,
										disabledTooltip: PERMISSION_DENIED_MESSAGE,
										onClick: async () => { onClickEditButton?.() }
									},
									{
										title: "Excluir",
										icon: <DeleteIcon />,
										showIcon: true,
										color: "danger" as const,
										disabled: !canDeleteAttendant || isOwner,
										disabledTooltip: isOwner
											? "Não é possível excluir o dono da conta"
											: PERMISSION_DENIED_MESSAGE,
										onClick: async () => { onClickDeleteButton?.() }
									}
								]}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Portlet>
	)
}

export default UserInInstanceCard
